/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {ModelResponse, nullify} from '@dvrd/dvr-controls';
import {ResponseData} from '@dvrd/fetch';
import {WaitlistDay} from '../enums';
import {createWaitlistEntry, getWaitlistEntries, updateWaitlistEntry} from '../util/requests';
import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

interface Props extends BaseProps {
    days?: Array<WaitlistDay>;
    index?: number;
    customer_name?: string | null;
    dog_name?: string | null;
    tel?: string | null;
    email?: string | null;
    comment?: string | null;
}

export interface WaitlistEntryFields extends BaseFields {
    days: Array<WaitlistDay>;
    index: number;
    customer_name: string | null;
    dog_name: string | null;
    tel: string | null;
    email: string | null;
    comment: string | null;
}

const fields: WaitlistEntryFields = {
    id: '',
    created_at: '',
    updated_at: '',
    days: [],
    index: -1,
    customer_name: null,
    dog_name: null,
    tel: null,
    email: null,
    comment: null
};

type FieldDefType = keyof WaitlistEntryFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, null, true),
    days: createFieldDef(ModelFieldType.ARRAY),
    index: createFieldDef(ModelFieldType.NUMBER, -1),
    customer_name: createFieldDef(ModelFieldType.STRING, null, true),
    dog_name: createFieldDef(ModelFieldType.STRING, null, true),
    tel: createFieldDef(ModelFieldType.STRING, null, true),
    email: createFieldDef(ModelFieldType.STRING, null, true),
    comment: createFieldDef(ModelFieldType.STRING, null, true)
};

const skipFields: Array<keyof WaitlistEntryFields> = [];

export default class WaitlistEntry extends BaseModel<Props, WaitlistEntryFields, FieldDefType> {
    static getAll = (callback: ModelResponse<Array<WaitlistEntry>>) => {
        getWaitlistEntries({
            callback: (data: ResponseData) => {
                const entries = data.success ? data.entries.map((ent: any) => new WaitlistEntry(ent)) : [];
                callback(entries, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(skipFields, fieldsDefinition, fields, props);
        this.build(props);
    }

    get days(): Array<WaitlistDay> {
        return this.fields.days;
    }

    set days(days: Array<WaitlistDay>) {
        this.fields.days = days;
    }

    get index(): number {
        return this.fields.index;
    }

    set index(value: number) {
        this.fields.index = value;
    }

    get customerName(): string | null {
        return this.fields.customer_name;
    }

    set customerName(name: string | null) {
        this.fields.customer_name = name;
    }

    get dogName(): string | null {
        return this.fields.dog_name;
    }

    set dogName(value: string | null) {
        this.fields.dog_name = value;
    }

    get tel(): string | null {
        return this.fields.tel;
    }

    set tel(value: string | null) {
        this.fields.tel = value;
    }

    get email(): string | null {
        return this.fields.email;
    }

    set email(value: string | null) {
        this.fields.email = value;
    }

    get comment(): string | null {
        return this.fields.comment;
    }

    set comment(value: string) {
        this.fields.comment = value;
    }

    get copy(): WaitlistEntry {
        const fields: WaitlistEntryFields = {
            comment: this.comment, created_at: this.createdAt, customer_name: this.customerName,
            days: this.days.slice(), dog_name: this.dogName, email: this.email, id: this.id, index: this.index,
            tel: this.tel, updated_at: this.updatedAt
        };
        return new WaitlistEntry(fields);
    }

    hasExtraData = (): boolean => {
        return !!this.email || !!this.tel || !!this.comment;
    };

    create = (callback: ModelResponse<WaitlistEntry>) => {
        createWaitlistEntry({
            data: {
                comment: nullify(this.comment), customer_name: nullify(this.customerName), days: this.days,
                dog_name: nullify(this.dogName), email: nullify(this.email), tel: nullify(this.tel)
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.waitlist_entry);
                callback(this, data.success, data);
            }
        });
    };

    update = (callback: ModelResponse<WaitlistEntry>) => {
        return updateWaitlistEntry({
            data: {
                tel: nullify(this.tel), email: nullify(this.email), dog_name: nullify(this.dogName),
                days: this.days, customer_name: nullify(this.customerName), comment: nullify(this.comment),
                entry_id: this.id
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.waitlist_entry);
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
        }
    };
}